import React, { Fragment, useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import Chart from "chart.js/auto";
import Card from "./Card";
import RadarChart from "./RadarChart";
import Consequences from "./Consequences";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong, faChartBar, faFlagCheckered } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const listSectors = ["agriculture", "industry", "public", "consumer"];

const GraphGame = ({
  data,
  decision_metrics_data,
  goBackToGame,
  status,
  shortTermNeeds,
  showAdditionalScore,
  language,
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (language) i18n.changeLanguage(language);
  }, [language, i18n]);

  const { gameId } = useParams();
  const navigate = useNavigate();
  const [showConsequences, setShowConsequences] = useState(false);
  const [consequences, setConsequences] = useState([]);

  const fetchAndShowConsequences = async () => {
    if (consequences.length === 0) {
      const data = await fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/get_consequences`).then(r => r.json());
      setConsequences(data);
    }
    setShowConsequences(true);
  };

  const startYear = new Date(new Date().setMonth(new Date().getMonth() + 2)).getFullYear();

  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedDecision, setSelectedDecision] = useState(null);

  const roundedTopPlugin = {
    id: "roundedTop",
    beforeDatasetsDraw(chart) {
      const datasets = chart.data.datasets;
      const barDatasets = datasets
        .map((d, i) => ({ d, i, meta: chart.getDatasetMeta(i) }))
        .filter(({ meta }) => meta.type === "bar");

      barDatasets.forEach(({ d, i, meta }) => {
        meta.data.forEach((bar, colIndex) => {
          const isTop = !barDatasets.some(
            ({ d: otherD, i: otherI }) =>
              otherI > i && (otherD.data[colIndex] ?? 0) > 0
          );
          if (isTop && (d.data[colIndex] ?? 0) > 0) {
            bar.options.borderRadius = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };
            bar.options.borderSkipped = false;
          } else {
            bar.options.borderRadius = 0;
          }
        });
      });
    },
  };

  const handleClick = (Part, Sector) => {
    if (selectedPart && selectedPart.Part === Part && selectedPart.Sector === Sector) {
      setSelectedPart(null);
      setSelectedDecision(null);
    } else {
      setSelectedPart({ Part, Sector });
      setSelectedDecision(null);
    }
  };

  const handleClickPieChart = (decision) => {
    setSelectedDecision(selectedDecision?._id === decision._id ? null : decision);
  };

  const finishGame = async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/finish_game`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    navigate(`/game/${gameId}/summary`);
  };

  const goToSummary = () => navigate(`/game/${gameId}/summary`);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/download_report`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `game_report_${gameId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement du rapport :", error);
    }
  };

  const barChartOptions = {
    onClick: (e, element) => {
      if (element.length > 0) {
        const { datasetIndex: sectorIndex, index: tourIndex } = element[0];
        if (sectorIndex >= listSectors.length) return;
        const Part = data.filter((item) => item.tour === tourIndex)[0];
        if (!Part) return;
        const Sector = listSectors[sectorIndex];
        handleClick(Part, Sector);
      }
    },
    plugins: {
      datalabels: false,
      legend: {
        position: "bottom",
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: "rectRounded",
          font: { size: 12 },
          color: "#205673",
        },
      },
      tooltip: {
        backgroundColor: "rgba(32, 86, 115, 0.92)",
        titleColor: "#fff",
        bodyColor: "rgba(255,255,255,0.85)",
        cornerRadius: 8,
        padding: 10,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: t("Tours"), color: "#205673", font: { size: 12, weight: "600" } },
        grid: { display: false },
        ticks: { color: "#205673" },
      },
      y: {
        stacked: true,
        title: { display: true, text: t("Économie d'eau"), color: "#205673", font: { size: 12, weight: "600" } },
        grid: { color: "rgba(32, 86, 115, 0.08)" },
        ticks: { color: "#205673" },
      },
    },
  };

  const nbRounds = status.nbRounds ?? 5;
  const totalYears = nbRounds + 5;

  const barChartData = status.finishGraph
    ? {
        labels: status.finishGraph.map((item) => item.tour + 1),
        datasets: [
          {
            label: t("Agriculture"),
            backgroundColor: Array(totalYears).fill("").map((_, i) =>
              status.roundNumber > i ? "#b7e0e1" : "rgba(183,224,225,0.3)"
            ),
            data: status.finishGraph.map((item) => item.agriculture.total),
          },
          {
            label: t("Industrie"),
            backgroundColor: Array(totalYears).fill("").map((_, i) =>
              status.roundNumber > i ? "#205673" : "rgba(32,86,115,0.3)"
            ),
            data: status.finishGraph.map((item) => item.industry.total),
          },
          {
            label: t("Public"),
            backgroundColor: Array(totalYears).fill("").map((_, i) =>
              status.roundNumber > i ? "#db6c52" : "rgba(219,108,82,0.3)"
            ),
            data: status.finishGraph.map((item) => item.public.total),
          },
          {
            label: t("Consommateurs"),
            backgroundColor: Array(totalYears).fill("").map((_, i) =>
              status.roundNumber > i ? "#f5c842" : "rgba(245,200,66,0.3)"
            ),
            data: status.finishGraph.map((item) => item.consumer.total),
          },
          {
            label: t("Courbe d'objectif"),
            data: Array.from({ length: totalYears }, (_, i) => {
              const x = i + 1;
              const target = 20 * nbRounds;
              const workshopDone = Math.min(status.roundNumber, nbRounds);
              if (x < nbRounds) {
                return { x, y: (target * x * Math.min(workshopDone, x)) / (nbRounds * nbRounds) };
              } else {
                const tailDivisors = [nbRounds + 1, nbRounds + 0.5, nbRounds + 0.3, nbRounds + 0.2, nbRounds + 0.1, nbRounds];
                const divisor = tailDivisors[x - nbRounds] ?? nbRounds;
                return { x, y: Math.round((target * workshopDone) / divisor) };
              }
            }),
            type: "line",
            fill: false,
            borderColor: "#e74c3c",
            borderWidth: 2,
            borderDash: [6, 4],
            pointStyle: "circle",
            pointRadius: 4,
            pointBackgroundColor: "#e74c3c",
            tension: 0.3,
          },
        ],
      }
    : null;

  const listSelectedDecision =
    selectedPart?.Part?.[selectedPart.Sector]?.decision_list ?? [];

  const pieChartData = {
    labels: listSelectedDecision.map((item) => item.title),
    datasets: [
      {
        data: listSelectedDecision.map((item) => item.contribution),
        backgroundColor: [
          "rgba(255, 99, 132, 0.8)",
          "rgba(54, 162, 235, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(210, 99, 132, 0.8)",
          "rgba(54, 190, 145, 0.8)",
          "rgba(29, 206, 106, 0.8)",
        ],
      },
    ],
  };

  const pieChartOptions = {
    onClick: (e, element) => {
      if (element.length > 0) handleClickPieChart(listSelectedDecision[element[0].index]);
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: { datalabels: false },
  };

  const isLastRound = status.roundNumber === nbRounds;

  return (
    <div className="gg-page">

      {/* ── Header ── */}
      <div className="gg-header">
        <div className="gg-header-left">
          <span className="gg-tour-badge">
            {isLastRound
              ? <><FontAwesomeIcon icon={faFlagCheckered} style={{ marginRight: 8 }} />{t("Fin de partie")}</>
              : `${t("Tour")} ${status.roundNumber + 1}`}
          </span>
          {isLastRound && status.objectivesStatus === "bonne-voie" && (
            <span className="gg-success-badge">{t("congratulations")}</span>
          )}
        </div>
        <div className="gg-header-right">
          <FontAwesomeIcon icon={faChartBar} style={{ color: "var(--hydros-orange)", marginRight: 8 }} />
          <span className="gg-header-subtitle">{t("Récapitulatif")}</span>
        </div>
      </div>

      {/* ── Score final ── */}
      {isLastRound && (() => {
        const score = Math.round((100 - status.prevision) / (20 * nbRounds) * 100);
        const scoreClass = score >= 100 ? "gg-score-hero--success" : score >= 90 ? "gg-score-hero--warning" : "gg-score-hero--fail";
        return (
          <div className={`gg-score-hero ${scoreClass}`}>
            <div className="gg-score-circle">
              <span className="gg-score-number">{score}</span>
              <span className="gg-score-unit">%</span>
            </div>
            <div className="gg-score-label">
              <span className="gg-score-title">{score >= 100 ? t("congratulations") : t("endOfLastRound")}</span>
              <span className="gg-score-sub">{t("waterSavingsRecap")}</span>
            </div>
          </div>
        );
      })()}

      {/* ── Narrative ── */}
      {!isLastRound && (
        <div className="gg-narrative">
          <p className="gg-narrative-text">
            {t("welcomeMessage", {
              year: startYear + status.roundNumber,
              congressNumber: status.roundNumber + 1,
            })}
          </p>

          {status.shortTermNeeds <= 0 && (
            <p className="gg-narrative-text" style={{ marginTop: 12 }}>
              {t("objectivesStatus", {
                objectives: t(status.objectivesStatus),
                droughtType: t(status.droughtType),
                measuresTerm: t(status.measuresTerm),
              })}
            </p>
          )}

          {status.shortTermNeeds > 0 && (
            <div className="gg-alert-block">
              <p className="gg-narrative-text">{t("lowPrecipitation")}</p>
              <img
                src={
                  status?.display_metadata?.custom_drought_map
                    ? status.display_metadata.custom_drought_map
                    : t("map_meteo_crisis")
                }
                className="gg-meteo-img"
                alt="carte météo"
              />
              <p className="gg-narrative-text">
                {t("shortTermNeeds", { needs: status.shortTermNeeds })}{" "}
                <img className="water-budget-drop-small" src="/hydros_logo.png" alt={t("waterDrop")} />{" "}
                {t("nextTurn")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Charts ── */}
      <div className={`gg-charts-grid ${showAdditionalScore ? "gg-charts-grid--two" : "gg-charts-grid--one"}`}>
        <div className="gg-chart-card">
          <div className="gg-chart-card-header">
            <span className="gg-chart-card-title">{t("waterSavingsEvolution")}</span>
            <span className="gg-chart-hint">{t("Cliquez sur une barre pour les détails")}</span>
          </div>
          <div className="gg-chart-inner">
            {barChartData && <Bar data={barChartData} options={barChartOptions} plugins={[roundedTopPlugin]} />}
          </div>
        </div>

        {showAdditionalScore && (
          <div className="gg-chart-card">
            <div className="gg-chart-card-header">
              <span className="gg-chart-card-title">{t("otherIndicators")}</span>
            </div>
            <div className="gg-chart-inner gg-chart-inner--radar">
              <RadarChart data_radar={decision_metrics_data} t={t} />
            </div>
          </div>
        )}
      </div>

      {/* ── Drill-down: pie + decision card ── */}
      {selectedPart && (
        <div className="gg-detail-row">
          <div className="gg-chart-card gg-pie-card">
            <div className="gg-chart-card-header">
              <span className="gg-chart-card-title">
                {t("Décisions")} - {t(selectedPart.Sector)}, {t("Tour")} {selectedPart.Part.tour + 1}
              </span>
              <span className="gg-chart-hint">{t("Cliquez sur un segment pour le détail")}</span>
            </div>
            <div className="gg-chart-inner gg-pie-inner">
              <Pie data={pieChartData} options={pieChartOptions} />
            </div>
          </div>

          {selectedDecision && (
            <div className="gg-decision-card-wrap">
              <Card decision={selectedDecision} selected={false} handleSelect={() => {}} t={t} />
            </div>
          )}
        </div>
      )}

      {/* ── Actions ── */}
      <div className="gg-actions">
        {isLastRound ? (
          <>
            <button className="gg-btn gg-btn-back" onClick={fetchAndShowConsequences}>
              {t("Voir les conséquences")}
            </button>
            {status.isFinished ? (
              <button className="gg-btn gg-btn-primary" onClick={goToSummary}>
                {t("gameSummary")}
              </button>
            ) : (
              <button className="gg-btn gg-btn-primary" onClick={finishGame}>
                {t("accessSummary")}
              </button>
            )}
          </>
        ) : (
          <button className="gg-btn gg-btn-back" onClick={goBackToGame}>
            {t("reviewPastEvents")}
            <FontAwesomeIcon icon={faArrowRightLong} />
          </button>
        )}
      </div>

      {showConsequences && consequences.length > 0 && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "#fff",
            zIndex: 1000,
            overflowY: "auto",
          }}
        >
          <button
            onClick={() => setShowConsequences(false)}
            style={{
              position: "fixed", top: 16, right: 16, zIndex: 1001,
              background: "#205673", color: "#fff", border: "none",
              borderRadius: "50%", width: 36, height: 36,
              cursor: "pointer", fontSize: 18, lineHeight: "36px", textAlign: "center",
            }}
          >✕</button>
          <Consequences
            consequences={consequences}
            hideConsequences={() => setShowConsequences(false)}
          />
        </div>
      )}
    </div>
  );
};

export default GraphGame;
